<template>
	<div class="c-admin-signup-chart">
		<p class="c-admin-signup-chart__detail" role="status">
			<template v-if="activePoint">
				<strong>{{ activePoint.date }} (UTC)</strong>: {{ activePoint.value }}
				{{ cumulative ? 'cumulative ' : '' }}{{ label }}
			</template>
			<template v-else>Hover, tap, or focus a day to see its count.</template>
		</p>
		<div class="c-admin-signup-chart__scroll">
			<svg
				viewBox="0 0 720 300"
				role="group"
				:aria-label="chartTitle"
			>
				<title>
					{{ cumulative ? 'Cumulative' : 'Daily' }} {{ chartTitle }}
				</title>
				<g v-for="tick in ticks" :key="tick.value">
					<line x1="48" x2="700" :y1="tick.y" :y2="tick.y" class="grid" />
					<text x="40" :y="tick.y + 4" text-anchor="end">{{ tick.value }}</text>
				</g>
				<polyline :points="linePoints" class="series" />
				<g v-for="point in dateLabels" :key="point.date">
					<text :x="point.x" y="282" :text-anchor="point.anchor">
						{{ point.label }}
					</text>
				</g>
				<circle
					v-if="activePoint"
					:cx="activePoint.x"
					:cy="activePoint.y"
					r="4"
					class="point"
				/>
				<rect
					v-for="(point, index) in points"
					:key="point.date"
					:x="point.x - pointSpacing / 2"
					y="16"
					:width="pointSpacing"
					height="244"
					class="hit-area"
					tabindex="0"
					:aria-label="`${point.date} UTC: ${point.value} ${cumulative ? 'cumulative ' : ''}${label}`"
					@mouseenter="activeIndex = index"
					@mouseleave="activeIndex = null"
					@focus="activeIndex = index"
					@blur="activeIndex = null"
					@click="activeIndex = index"
				>
					<title>{{ point.date }} UTC: {{ point.value }} {{ label }}</title>
				</rect>
			</svg>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		days: { type: Array, required: true },
		cumulative: { type: Boolean, default: false },
		label: { type: String, default: 'signups' },
		chartTitle: { type: String, default: 'Email-verified signups by signup date' },
	},
	data() {
		return { activeIndex: null };
	},
	computed: {
		values() {
			let total = 0;
			return this.days.map((day) => {
				total += day.count;
				return this.cumulative ? total : day.count;
			});
		},
		step() {
			let maximum = 0;
			for (const value of this.values) {
				maximum = Math.max(maximum, value);
			}
			return Math.max(1, Math.ceil(maximum / 4));
		},
		ticks() {
			const ticks = [];
			for (let i = 0; i <= 4; i++) {
				ticks.push({ value: i * this.step, y: 260 - i * 60 });
			}
			return ticks;
		},
		pointSpacing() {
			return 652 / Math.max(1, this.days.length - 1);
		},
		points() {
			return this.days.map((day, index) => ({
				date: day.date,
				value: this.values[index],
				x: 48 + index * this.pointSpacing,
				y: 260 - (this.values[index] / (this.step * 4)) * 240,
			}));
		},
		linePoints() {
			return this.points.map((point) => `${point.x},${point.y}`).join(' ');
		},
		dateLabels() {
			const interval = Math.max(1, Math.ceil((this.points.length - 1) / 4));
			return this.points
				.filter(
					(point, index) =>
						index % interval === 0 || index === this.points.length - 1,
				)
				.map((point) => ({
					date: point.date,
					label: point.date.slice(5),
					x: point.x,
					anchor: point.x === 48 ? 'start' : point.x === 700 ? 'end' : 'middle',
				}));
		},
		activePoint() {
			return this.activeIndex === null ? null : this.points[this.activeIndex];
		},
	},
};
</script>

<style lang="scss">
.c-admin-signup-chart {
	&__detail {
		min-height: 2.5em;
		margin: var(--margin-lg) 0 0;
		font-size: var(--font-size-sm);
	}

	&__scroll {
		overflow-x: auto;
	}

	svg {
		display: block;
		width: 100%;
		min-width: 520px;
	}

	text {
		fill: var(--color-font);
		font-size: 12px;
	}

	.grid {
		stroke: var(--color-bg-5);
		stroke-width: 1;
	}

	.series {
		fill: none;
		stroke: var(--color-primary);
		stroke-width: 2;
		vector-effect: non-scaling-stroke;
	}

	.point {
		fill: var(--color-primary);
	}

	.hit-area {
		fill: transparent;
		cursor: crosshair;

		&:focus {
			outline: none;
			stroke: var(--color-primary);
			stroke-width: 1;
		}
	}
}
</style>
